import React, { useEffect, useRef } from 'react';
import { StarIcon, CheckCircleIcon, ChevronRightIcon } from 'lucide-react';
import JsBarcode from 'jsbarcode';
interface LoyaltyData {
  memberId?: string;
  pointsBalance?: number;
  status?: 'active' | 'inactive';
  tier?: 'silver' | 'gold' | 'platinum';
  isEnrolled?: boolean;
}
interface LoyaltyCardProps {
  progress?: number;
  onJoinNow?: () => void;
  onViewDetails?: () => void;
  loyaltyData?: LoyaltyData;
  variant?: 'default' | 'compact';
}
export function LoyaltyCard({
  progress = 0,
  onJoinNow,
  onViewDetails,
  loyaltyData,
  variant = 'default'
}: LoyaltyCardProps) {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const isEnrolled = loyaltyData?.isEnrolled && progress === 100;
  // Generate barcode when in member state
  useEffect(() => {
    if (isEnrolled && loyaltyData?.memberId && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, loyaltyData.memberId, {
          format: 'CODE128',
          width: 2.5,
          height: 80,
          displayValue: false,
          background: '#ffffff',
          lineColor: '#000000',
          margin: 10
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [isEnrolled, loyaltyData?.memberId]);
  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'platinum':
        return 'text-gray-300';
      case 'gold':
        return 'text-yellow-300';
      case 'silver':
        return 'text-gray-400';
      default:
        return 'text-blue-200';
    }
  };
  const getTierLabel = (tier?: string) => {
    if (!tier) return 'Member';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };
  // Member Summary State
  if (isEnrolled) {
    if (variant === 'compact') {
      return <button onClick={onViewDetails} className="w-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-2.5 text-white shadow-md flex items-center justify-between" data-id="element-601" data-mp="30bvdxtqvfb2">
          <div className="flex items-center gap-3" data-id="element-602" data-mp="6hzmtrav09j2">
            <div className="w-7 h-7 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-603" data-mp="6ju7r5m4s1n5">
              <StarIcon className="w-3.5 h-3.5 text-[#1E2B55]" fill="currentColor" data-id="element-604" data-mp="1hv6c19ybesu" />
            </div>
            <div className="text-left" data-id="element-605" data-mp="3ooe1jsgycnii">
              <p className="text-xs font-bold leading-tight" data-id="element-606" data-mp="3opp1tib6vd4v">Rabeh Rewards</p>
              <p className="text-sm font-black text-[#FFD700] leading-tight" data-id="element-607" data-mp="p7pi0fijaxbi">
                {loyaltyData?.pointsBalance?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
          <div className="text-right" data-id="element-608" data-mp="fhw77gq7um8b">
            <p className="text-[10px] text-blue-200 uppercase font-bold tracking-wider leading-tight" data-id="element-609" data-mp="fguuz8xgrtp0">
              {getTierLabel(loyaltyData?.tier)}
            </p>
            <ChevronRightIcon className="w-4 h-4 text-white ml-auto opacity-70" data-id="element-610" data-mp="1801rdm7luyv5" />
          </div>
        </button>;
    }
    return <div onClick={onViewDetails} className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-4 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow" data-id="element-611" data-mp="136p35d6u8u2x">
        <div className="flex items-center gap-2 mb-4" data-id="element-612" data-mp="2uvy7tfdm3j52">
          <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center" data-id="element-613" data-mp="2uv6ph690zhwq">
            <StarIcon className="w-5 h-5 text-[#1E2B55]" fill="currentColor" data-id="element-614" data-mp="2zvub1cehj6jk" />
          </div>
          <div className="flex-1" data-id="element-615" data-mp="34t2tzf7gjelv">
            <h3 className="text-base font-semibold" data-id="element-616" data-mp="34s1k3e26gy5q">Rabeh Rewards</h3>
            <div className="flex items-center gap-1 mt-0.5" data-id="element-617" data-mp="39rxnpc6yqnkr">
              <CheckCircleIcon className="w-3 h-3 text-green-400" data-id="element-618" data-mp="3ejfej7f9v7s0" />
              <span className="text-xs text-green-300" data-id="element-619" data-mp="3em1v1wk54xmd">Active Member</span>
            </div>
          </div>
          <ChevronRightIcon className="w-5 h-5 text-white opacity-70" data-id="element-620" data-mp="3inde1j8iekll" />
        </div>
        {/* Member Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-621" data-mp="3sjgp5elc9tko">
          <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-622" data-mp="3sjzyw80u5bnm">
            <p className="text-xs text-blue-200 mb-1" data-id="element-623" data-mp="3evoprokrzrgb">Points Balance</p>
            <p className="text-xl font-bold text-[#FFD700]" data-id="element-624" data-mp="39yol9i3kqwrb">
              {loyaltyData?.pointsBalance?.toLocaleString() || '0'}
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-625" data-mp="3oongpd3qvu33">
            <p className="text-xs text-blue-200 mb-1" data-id="element-626" data-mp="3johu4bcc0xrv">Membership Tier</p>
            <p className={`text-xl font-bold ${getTierColor(loyaltyData?.tier)}`} data-id="element-627" data-mp="2vywzuwaqae3a">
              {getTierLabel(loyaltyData?.tier)}
            </p>
          </div>
        </div>
        {/* Status */}
        <div className="mb-4" data-id="element-628" data-mp="35xmkgwu0ecn8">
          <div className="flex items-center justify-between text-xs" data-id="element-629" data-mp="30x8d7h08f6q9">
            <span className="text-blue-200" data-id="element-630" data-mp="30zba8kzsy04h">Status</span>
            <span className={`font-semibold ${loyaltyData?.status === 'active' ? 'text-green-300' : 'text-red-300'}`} data-id="element-631" data-mp="l5awifqro3a7">
              {loyaltyData?.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        {/* Barcode Section */}
        {loyaltyData?.memberId && <div className="bg-white rounded-lg p-4 text-center" data-id="element-632" data-mp="sqwp0pgroidw">
            <div className="flex items-center justify-center mb-3" data-id="element-633" data-mp="eqvojb4ezcyy">
              <svg ref={barcodeRef} className="max-w-full h-auto" data-id="element-634" data-mp="ir5rsg0tnpa4"></svg>
            </div>
            <p className="text-sm text-gray-700 font-mono tracking-wider" data-id="element-635" data-mp="16h03gu9en7ss">
              Member ID: {loyaltyData.memberId}
            </p>
          </div>}
      </div>;
  }
  // Onboarding State (existing behavior)
  if (variant === 'compact') {
    return <button onClick={onJoinNow} className="w-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-2.5 text-white shadow-md flex items-center justify-between" data-id="element-636" data-mp="xog1pkxtgn3q">
        <div className="flex items-center gap-3" data-id="element-637" data-mp="1peb0073njn4j">
          <div className="w-7 h-7 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-638" data-mp="1u74731mlf7js">
            <StarIcon className="w-3.5 h-3.5 text-[#1E2B55]" fill="currentColor" data-id="element-639" data-mp="242ulzrotq0h4" />
          </div>
          <div className="text-left" data-id="element-640" data-mp="201r6x7elo8gy">
            <p className="text-xs font-bold leading-tight" data-id="element-641" data-mp="1v1mp9e1yh7p5">
              Join Rabeh Rewards
            </p>
            <p className="text-[10px] text-blue-100 leading-tight mt-0.5" data-id="element-642" data-mp="1q3bff3bnux6x">
              Earn points on every order
            </p>
          </div>
        </div>
        <ChevronRightIcon className="w-4 h-4 text-white opacity-70" data-id="element-643" data-mp="1l5tf21bplqaj" />
      </button>;
  }
  return <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-4 text-white" data-id="element-644" data-mp="1gavxu74a0amk">
      <div className="flex items-center gap-2 mb-3" data-id="element-645" data-mp="1c9aogvqyaeh4">
        <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center" data-id="element-646" data-mp="17d3ix42gv4zp">
          <StarIcon className="w-5 h-5 text-[#1E2B55]" fill="currentColor" data-id="element-647" data-mp="12dh3qp32veo9" />
        </div>
        <h3 className="text-base font-bold" data-id="element-648" data-mp="xdmd8mn7sr3q">Rabeh Rewards</h3>
      </div>
      <p className="text-xs text-blue-100 mb-3" data-id="element-649" data-mp="xggz8ilvmjz9">Profile {progress}% complete</p>
      <div className="mb-4" data-id="element-650" data-mp="n9xwhe2jjpuy">
        <div className="flex justify-between items-center mb-2" data-id="element-651" data-mp="s9ajbytewwxj">
          <span className="text-xs text-blue-100" data-id="element-652" data-mp="s7fvocqraqpr">Progress</span>
          <span className="text-sm font-bold text-[#FFD700]" data-id="element-653" data-mp="x9ez1mmhxnmp">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-white bg-opacity-20 rounded-full overflow-hidden" data-id="element-654" data-mp="127qa20oj246i">
          <div className="h-full bg-[#FFD700] transition-all duration-500" style={{
          width: `${progress}%`
        }} data-id="element-655" data-mp="4k804mj4qw0x" />
        </div>
      </div>
      <p className="text-xs text-blue-100 mb-3" data-id="element-656" data-mp="ehcei06qhn76">
        Complete your profile to unlock full benefits
      </p>
      <button onClick={onJoinNow} className="w-full bg-white text-[#1E2B55] text-sm font-bold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-657" data-mp="j8ucqghdh9og">
        {progress === 0 ? 'Join Now' : 'Complete Profile'}
      </button>
    </div>;
}